'use client'

import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { Info } from 'lucide-react'
import clsx from 'clsx'

import type { TooltipProps } from './types'

const VIEWPORT_MARGIN_PX = 16

export default function Tooltip({ label, triggerLabel = 'More information', className }: TooltipProps) {
	const [isOpen, setIsOpen] = useState(false)
	const panelId = useId()
	const rootRef = useRef<HTMLDivElement>(null)
	const triggerRef = useRef<HTMLButtonElement>(null)
	const panelRef = useRef<HTMLDivElement>(null)

	function close() {
		setIsOpen(false)
	}

	function handleTriggerClick() {
		setIsOpen((open) => !open)
	}

	function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
		if (event.key === 'Escape' && isOpen) {
			event.preventDefault()
			close()
			triggerRef.current?.focus()
		}
	}

	// Click-outside closes the panel for pointer users — Escape (above) covers keyboard users.
	useEffect(() => {
		if (!isOpen) return

		function handlePointerDown(event: MouseEvent) {
			if (rootRef.current && !rootRef.current.contains(event.target as Node)) close()
		}

		document.addEventListener('mousedown', handlePointerDown)
		return () => document.removeEventListener('mousedown', handlePointerDown)
	}, [isOpen])

	// The panel defaults to left-aligned with the trigger (see .form-tooltip-panel), which
	// overflows the right edge for a trigger sitting after a long label. Shifting it purely
	// with a right-anchor instead overflows the left edge for short labels, since the trigger
	// then sits close to the row's start. Neither static CSS anchor covers both cases, so the
	// offset is measured against the viewport and kept clamped at all times — not just while
	// open: the panel stays in the DOM (just `invisible`, see .form-tooltip-panel--closed) so
	// an unclamped closed panel still counts toward the page's scrollable width and can widen
	// the whole mobile layout even though nothing is visibly showing.
	// Layout effect (not a regular effect) so the corrected position is applied before the
	// browser paints — otherwise it flashes at the left-0 default first, which is especially
	// jarring on narrow screens where the correction is large.
	useLayoutEffect(() => {
		const container = rootRef.current
		const panel = panelRef.current
		if (!container || !panel) return

		function reposition() {
			const containerLeft = container!.getBoundingClientRect().left
			const panelWidth = panel!.offsetWidth
			// `clientWidth` (not `window.innerWidth`) — it's the actual rendered layout
			// viewport, excluding scrollbars and immune to the innerWidth/visual-viewport
			// mismatch some emulated/zoomed contexts introduce.
			const viewportWidth = document.documentElement.clientWidth
			let left = 0
			const overflowRight = containerLeft + panelWidth - (viewportWidth - VIEWPORT_MARGIN_PX)
			if (overflowRight > 0) left -= overflowRight
			const minLeft = VIEWPORT_MARGIN_PX - containerLeft
			if (left < minLeft) left = minLeft
			panel!.style.left = `${left}px`
		}

		reposition()
		window.addEventListener('resize', reposition)
		return () => window.removeEventListener('resize', reposition)
	}, [isOpen])

	return (
		<div ref={rootRef} className={clsx('form-tooltip', className)} onKeyDown={handleKeyDown}>
			<button
				ref={triggerRef}
				type="button"
				aria-expanded={isOpen}
				aria-controls={panelId}
				aria-label={triggerLabel}
				onClick={handleTriggerClick}
				className="form-tooltip-trigger"
			>
				<Info aria-hidden="true" className="form-tooltip-icon" />
			</button>

			<div
				ref={panelRef}
				id={panelId}
				role="tooltip"
				{...(!isOpen ? ({ inert: '' } as Record<string, string>) : {})}
				className={clsx('form-tooltip-panel', isOpen ? 'form-tooltip-panel--open' : 'form-tooltip-panel--closed')}
			>
				{label}
			</div>
		</div>
	)
}
